import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@mui/material";
import { Lock } from "lucide-react";
import { toast } from "react-toastify";
import { ResponsiveDataList } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import { SectionShell } from "../../lifecycle/SectionShell";
import { ClosingRetreatCard } from "../cards/ClosingRetreatCard";
import { SECTION_IDS } from "../manageRetreatTypes";
import { dashboardContainedButtonSx } from "../dashboardUi";

export function ClosingSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  dataFetchApi,
  isSuperAdmin,
  actor,
  retreatApi,
  reloadAll,
  onHistory,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  dataFetchApi: (...args: any[]) => void;
  isSuperAdmin: boolean;
  actor: () => Record<string, unknown>;
  retreatApi: { completeRetreat: (payload: any) => Promise<any> };
  reloadAll: () => Promise<void>;
  onHistory: (r: LifecycleRetreat) => void;
}) {
  const navigate = useNavigate();

  return (
    <SectionShell
      id={SECTION_IDS.closing}
      title="Post-Retreat Closing"
      subtitle="Closure requirements, reminders, and temporary-access expiration are managed separately from active retreats."
      bgcolor="#f7f1fb"
      icon={<Lock size={22} color="#70428d" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      action={
        isSuperAdmin ? (
          <Button
            variant="contained"
            onClick={() => navigate("/retreat-closure-list")}
            sx={dashboardContainedButtonSx}
          >
            Closing Tools
          </Button>
        ) : undefined
      }
    >
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        countPerPage={50}
        dataFetchApi={dataFetchApi}
        forceCompact
        columns={[]}
        cardGridColumns={{ xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}
        noDataComponent={
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No retreats in closing progress.
          </Typography>
        }
        renderCard={(r) => {
          const missing = r.closingMissing || [];
          const canComplete = isSuperAdmin && missing.length === 0;
          return (
            <ClosingRetreatCard
              r={r}
              isSuperAdmin={isSuperAdmin}
              canComplete={canComplete}
              onHistory={() => onHistory(r)}
              completeRetreat={async () => {
                const res = await retreatApi.completeRetreat({
                  id: r.id,
                  ...actor(),
                });
                if (res?.success) {
                  toast.success(res.msg);
                  await reloadAll();
                } else toast.error(res?.msg || "Failed");
              }}
            />
          );
        }}
      />
    </SectionShell>
  );
}
